import { X, Copy, Check, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import type { ParcelAttribution } from "../types/agricultural-lulc";
import {
  LULC_COLORS,
  CROP_COLORS,
  FOREST_COLORS,
} from "../config/agricultural-lulc";
import type {
  LulcClass,
  CropClass,
  ForestClass,
} from "../types/agricultural-lulc";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";

interface ParcelDetailPanelProps {
  parcel: ParcelAttribution;
  onClose: () => void;
}

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
    >
      {id}
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </button>
  );
}

function ChangeIndicator({
  current,
  previous,
}: {
  current: number;
  previous: number;
}) {
  const diff = current - previous;
  const rounded = Math.round(diff * 100) / 100;
  if (rounded === 0) return <Minus className="size-3 text-muted-foreground" />;
  if (rounded > 0)
    return (
      <span className="inline-flex items-center text-xs text-success">
        <ArrowUp className="size-3" />
        {rounded.toFixed(2)}%
      </span>
    );
  return (
    <span className="inline-flex items-center text-xs text-destructive">
      <ArrowDown className="size-3" />
      {Math.abs(rounded).toFixed(2)}%
    </span>
  );
}

export function ParcelDetailPanel({ parcel, onClose }: ParcelDetailPanelProps) {
  // Build LULC comparison data
  const allLulcClasses = new Set([
    ...parcel.lulc_2024.map((l) => l.land_use),
    ...parcel.lulc_2025.map((l) => l.land_use),
  ]);

  const lulcComparison = Array.from(allLulcClasses).map((landUse) => {
    const y2024 = parcel.lulc_2024.find((l) => l.land_use === landUse);
    const y2025 = parcel.lulc_2025.find((l) => l.land_use === landUse);
    return {
      land_use: landUse,
      pct_2024: y2024?.coverage_pct ?? 0,
      area_2024: y2024?.coverage_area_sqm ?? 0,
      pct_2025: y2025?.coverage_pct ?? 0,
      area_2025: y2025?.coverage_area_sqm ?? 0,
    };
  });

  return (
    <Card className="w-xs lg:w-sm backdrop-blur supports-backdrop-filter:bg-card/90 overflow-hidden">
      {/* Header */}
      <CardHeader>
        <CardTitle>Parcel Detail</CardTitle>
        <CardDescription>
          <CopyableId id={parcel.cadastre_polygon_pid} />
        </CardDescription>
        <CardAction>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </CardAction>
      </CardHeader>

      {/* Area stats */}
      <CardContent className="space-y-2">
        <Item className="py-0">
          <ItemContent>
            <ItemTitle>Area</ItemTitle>
            <ItemDescription>
              {parcel.area_sqm.toLocaleString()} m²
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item className="py-0">
          <ItemContent>
            <ItemTitle>Hectares</ItemTitle>
            <ItemDescription>{parcel.area_ha} ha</ItemDescription>
          </ItemContent>
        </Item>

        {/* Tabs */}
        <Tabs
          defaultValue="lulc"
          className="flex-1 overflow-hidden flex flex-col"
        >
          <TabsList className="">
            <TabsTrigger value="lulc">LULC</TabsTrigger>
            <TabsTrigger value="crops">Crops</TabsTrigger>
            <TabsTrigger value="forest">Forest</TabsTrigger>
          </TabsList>

          <TabsContent value="lulc" className="flex-1 overflow-auto">
            <p className="text-xs py-2 text-muted-foreground">
              Year-over-year land use comparison
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Land Use</TableHead>
                  <TableHead className="text-xs text-right">2024</TableHead>
                  <TableHead className="text-xs text-right">2025</TableHead>
                  <TableHead className="text-xs text-right">Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lulcComparison.map((row) => (
                  <TableRow key={row.land_use}>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1.5">
                        <div
                          className="size-2.5 rounded-sm shrink-0"
                          style={{
                            backgroundColor:
                              LULC_COLORS[row.land_use as LulcClass],
                          }}
                        />
                        {row.land_use}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {row.pct_2024}%
                    </TableCell>
                    <TableCell className="text-xs text-right">
                      {row.pct_2025}%
                    </TableCell>
                    <TableCell className="text-right">
                      <ChangeIndicator
                        current={row.pct_2025}
                        previous={row.pct_2024}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="crops" className="flex-1 overflow-auto">
            {parcel.crops_2025.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                No crop data for this parcel
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Crop</TableHead>
                    <TableHead className="text-xs">Class</TableHead>
                    <TableHead className="text-xs text-right">
                      Coverage
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      Harvest
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parcel.crops_2025.map((crop) => (
                    <TableRow key={crop.crop_name}>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="size-2.5 rounded-sm shrink-0"
                            style={{
                              backgroundColor:
                                CROP_COLORS[crop.crop_name as CropClass],
                            }}
                          />
                          {crop.crop_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0"
                        >
                          {crop.class_main}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-right">
                        {crop.coverage_pct}%
                      </TableCell>
                      <TableCell className="text-xs text-right">
                        {crop.harvest_pct > 0 ? `${crop.harvest_pct}%` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="forest" className="flex-1 overflow-auto">
            {parcel.forest_2025.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">
                No forest data for this parcel
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Forest Class</TableHead>
                    <TableHead className="text-xs text-right">
                      Coverage
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parcel.forest_2025.map((f) => (
                    <TableRow key={f.forest_class}>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="size-2.5 rounded-sm shrink-0"
                            style={{
                              backgroundColor:
                                FOREST_COLORS[f.forest_class as ForestClass],
                            }}
                          />
                          {f.forest_class}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-right">
                        {f.coverage_pct}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
